import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/format";

export const ChangeRequestModal = ({ booking, open, onClose, onDone }) => {
  const [type, setType] = useState("edit");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setType("edit");
      setMessage("");
    }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/change-request`, { type, message });
      toast.success(
        type === "cancel"
          ? paid > 0
            ? "Cancellation request raised — our team will process your refund."
            : "Booking cancelled — no advance was paid, so there's nothing to refund."
          : "Change request sent — our team will update your booking."
      );
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not raise request");
    } finally {
      setBusy(false);
    }
  };

  const paid = booking?.amount_paid || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="change-request-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-ink">
            Edit or cancel booking
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Raise a change or cancellation request — our team reviews it and confirms with you.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 grid grid-cols-2 gap-2 rounded-full bg-cream-dark p-1 text-sm font-semibold">
          <button
            type="button"
            data-testid="change-type-edit"
            onClick={() => setType("edit")}
            className={`rounded-full py-2.5 transition-colors duration-200 ${
              type === "edit" ? "bg-navy text-cream" : "text-navy/60"
            }`}
          >
            Change details
          </button>
          <button
            type="button"
            data-testid="change-type-cancel"
            onClick={() => setType("cancel")}
            className={`rounded-full py-2.5 transition-colors duration-200 ${
              type === "cancel" ? "bg-navy text-cream" : "text-navy/60"
            }`}
          >
            Cancel booking
          </button>
        </div>

        <textarea
          data-testid="change-message"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={
            type === "edit"
              ? "Tell us what to change — dates, services, check-in time…"
              : "Optional note for our team…"
          }
          className="mt-5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-3 text-sm text-ink placeholder:text-slate-400 focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
        />

        {type === "cancel" && (
          <div data-testid="refund-terms" className="mt-4 rounded-2xl bg-cream-dark/60 p-4 text-xs leading-relaxed text-slate-600">
            <p className="font-semibold text-navy">Refund policy</p>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              <li>Cancelled more than 24 hours before check-in — 100% refund of the paid amount.</li>
              <li>Cancelled within 24 hours of check-in — 25% deduction, 75% refunded.</li>
            </ul>
            {paid > 0 ? (
              <p className="mt-2 text-slate-500">
                You have paid {fmtINR(paid)}. The exact refund is calculated when our team approves
                the request.
              </p>
            ) : (
              <p className="mt-2 font-medium text-amberwarm">
                No advance paid yet — this booking is cancelled straight away, with nothing to refund.
              </p>
            )}
          </div>
        )}

        <button
          type="button"
          data-testid="change-submit"
          onClick={submit}
          disabled={busy}
          className="mt-6 w-full rounded-full bg-bark px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-60"
        >
          {busy ? "Sending…" : type === "cancel" ? "Raise cancellation & refund request" : "Send change request"}
        </button>
        <p className="mt-3 text-center text-xs text-slate-400">
          Our team reviews every request and confirms on call or WhatsApp.
        </p>
      </DialogContent>
    </Dialog>
  );
};
